import React from 'react';
import { colors } from '../theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faApple, faGooglePlay } from '@fortawesome/free-brands-svg-icons';
import { faMobileAlt, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { getMobileOS, APP_STORE_URLS } from '../utils/mobileDetect';

interface MobileAppPromptProps {
  onContinueAnyway: () => void;
}

const MobileAppPrompt: React.FC<MobileAppPromptProps> = ({ onContinueAnyway }) => {
  const mobileOS = getMobileOS();
  
  const handleDownload = () => {
    if (mobileOS === 'iOS') {
      window.location.href = APP_STORE_URLS.iOS;
    } else if (mobileOS === 'Android') {
      window.location.href = APP_STORE_URLS.Android;
    }
  };

  const styles = {
    overlay: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primary} 100%)`,
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primary} 100%)`,
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px',
      overflow: 'auto',
    },
    pattern: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0.1,
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Cpath d='M30 30c0-11.046-8.954-20-20-20s-20 8.954-20 20 8.954 20 20 20 20-8.954 20-20zM10 30c0-11.046 8.954-20 20-20s20 8.954 20 20-8.954 20-20 20-20-8.954-20-20z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    },
    content: {
      position: 'relative' as const,
      zIndex: 1,
      textAlign: 'center' as const,
      maxWidth: '500px',
      width: '100%',
    },
    logo: {
        marginTop: '30px',
      marginBottom: '40px',
    },
    logoImage: {
      width: '120px',
      height: '120px',
      marginBottom: '20px',
    },
    icon: {
      fontSize: '80px',
      color: colors.white,
      marginBottom: '32px',
      animation: 'bounce 2s ease-in-out infinite',
    },
    title: {
      fontSize: '32px',
      fontWeight: '800',
      color: colors.white,
      marginBottom: '16px',
      textShadow: '0 2px 10px rgba(0,0,0,0.2)',
      lineHeight: '1.2',
    },
    subtitle: {
      fontSize: '18px',
      color: 'rgba(255,255,255,0.95)',
      marginBottom: '48px',
      lineHeight: '1.6',
      textShadow: '0 1px 4px rgba(0,0,0,0.2)',
    },
    buttonsContainer: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '16px',
      marginBottom: '32px',
    },
    storeButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '16px',
      padding: '18px 32px',
      backgroundColor: colors.white,
      color: colors.text,
      border: 'none',
      borderRadius: '16px',
      fontSize: '18px',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
    },
    storeButtonIcon: {
      fontSize: '28px',
    },
    continueButton: {
      padding: '12px 24px',
      backgroundColor: 'transparent',
      color: 'rgba(255,255,255,0.8)',
      border: '2px solid rgba(255,255,255,0.3)',
      borderRadius: '12px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '16px',
    },
    features: {
      marginTop: '40px',
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '12px',
    },
    feature: {
      color: 'rgba(255,255,255,0.9)',
      fontSize: '15px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    },
  };

  return (
    <>
      <style>
        {`
          @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-20px); }
          }
        `}
      </style>
      <div style={styles.overlay}>
        <div style={styles.pattern}></div>
        <div style={styles.content}>
          <div style={styles.logo}>
            <img src="/logo-white.png" alt="Property Assist Logo" style={styles.logoImage} />
          </div>

          <div style={styles.icon}>
            <FontAwesomeIcon icon={faMobileAlt} />
          </div>

          <h1 style={styles.title}>
            Téléchargez dès maintenant notre application !
          </h1>

          <p style={styles.subtitle}>
            Profitez de la meilleure expérience sur votre smartphone avec notre application native.
          </p>

          <div style={styles.buttonsContainer}>
            {mobileOS === 'iOS' && (
              <button
                style={styles.storeButton}
                onClick={handleDownload}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)';
                }}
              >
                <FontAwesomeIcon icon={faApple} style={styles.storeButtonIcon} />
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: '12px', opacity: 0.7 }}>Télécharger sur</div>
                  <div style={{ fontSize: '18px', fontWeight: '700' }}>App Store</div>
                </div>
              </button>
            )}

            {mobileOS === 'Android' && (
              <button
                style={styles.storeButton}
                onClick={handleDownload}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)';
                }}
              >
                <FontAwesomeIcon icon={faGooglePlay} style={styles.storeButtonIcon} />
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: '12px', opacity: 0.7 }}>Disponible sur</div>
                  <div style={{ fontSize: '18px', fontWeight: '700' }}>Google Play</div>
                </div>
              </button>
            )}

            {mobileOS === 'other' && (
              <>
                <button
                  style={styles.storeButton}
                  onClick={() => window.location.href = APP_STORE_URLS.iOS}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)';
                  }}
                >
                  <FontAwesomeIcon icon={faApple} style={styles.storeButtonIcon} />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: '12px', opacity: 0.7 }}>Télécharger sur</div>
                    <div style={{ fontSize: '18px', fontWeight: '700' }}>App Store</div>
                  </div>
                </button>

                <button
                  style={styles.storeButton}
                  onClick={() => window.location.href = APP_STORE_URLS.Android}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.2)';
                  }}
                >
                  <FontAwesomeIcon icon={faGooglePlay} style={styles.storeButtonIcon} />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: '12px', opacity: 0.7 }}>Disponible sur</div>
                    <div style={{ fontSize: '18px', fontWeight: '700' }}>Google Play</div>
                  </div>
                </button>
              </>
            )}
          </div>

          <div style={styles.features}>
            <div style={styles.feature}>
              ✨ Expérience optimisée pour mobile
            </div>
            <div style={styles.feature}>
              🚀 Performances améliorées
            </div>
            <div style={styles.feature}>
              📱 Notifications push en temps réel
            </div>
          </div>

          <button
            style={styles.continueButton}
            onClick={onContinueAnyway}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)';
              e.currentTarget.style.color = colors.white;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
              e.currentTarget.style.color = 'rgba(255,255,255,0.8)';
            }}
          >
            Continuer quand même <FontAwesomeIcon icon={faArrowRight} style={{ marginLeft: '8px' }} />
          </button>
        </div>
      </div>
    </>
  );
};

export default MobileAppPrompt;
